import { DateTime } from 'luxon';
import {
  BookingStatus,
  CleaningLevel,
  JobStatus,
  RangeStatus,
  ServiceSiteKind,
  ServiceType,
  VehicleClass,
  type IJobItemInput,
} from '../../../__generated__/graphql';
import type { BadgeTone } from '../../components/ui';
import type { IServiceJobBookingFieldsFragment } from '../../services/serviceService/__generated__/queries.generated';
import { formatMoney } from '../ServiceProviderPage/priceTables';
import { distanceWord, milesIn, sqftForSearch } from '../../utils/serviceUnits';
import { asDistanceUnit } from '../../utils/units';
import { defaultServiceSearch, type ServiceSearchState } from './serviceSearchState';

/**
 * Words and rules for the customer's side of a job (spec 019 W11, W15, W16).
 * Pure; the server decides prices, holds and what can be cancelled.
 */

export type ServiceJob = IServiceJobBookingFieldsFragment;

// ─── search results ─────────────────────────────────────────────────────────

export interface RangeView {
  label: string;
  tone: BadgeTone;
}

/**
 * The worker page's service area line, in the worker's unit (spec 028):
 * "About 15 miles around Phoenix 85004. Travels up to 30 miles for $2.00 a
 * mile past that."
 */
export function serviceAreaText(worker: {
  serviceRadiusMiles: number;
  distanceUnit?: string | null;
  city: string;
  zip: string;
  acceptsOutOfRadius: boolean;
  maxTravelMiles?: number | null;
  perMileBeyondRadius?: number | null;
}): string {
  const unit = asDistanceUnit(worker.distanceUnit);
  const many = distanceWord(unit);
  const one = distanceWord(unit, false);
  const base = `About ${worker.serviceRadiusMiles} ${many} around ${worker.city} ${worker.zip}.`;
  if (!worker.acceptsOutOfRadius) return base;
  return `${base} Travels up to ${worker.maxTravelMiles ?? worker.serviceRadiusMiles} ${many} for ${formatMoney(worker.perMileBeyondRadius ?? 0)} a ${one} past that.`;
}

/** The three range states of a worker card (D20); not-bookable words come from the server. */
export function rangeView(result: {
  rangeStatus: RangeStatus;
  distanceMiles: number;
  travelFee?: number | null;
  notBookableReason?: string | null;
  /** The radius is in the worker's `distanceUnit` (spec 028); `distanceMiles` in miles (D3). */
  worker?: { serviceRadiusMiles: number; distanceUnit?: string | null };
}): RangeView {
  switch (result.rangeStatus) {
    case RangeStatus.InRange:
      return { label: 'In the service area', tone: 'success' };
    case RangeStatus.OutOfRangeWithFee: {
      const unit = asDistanceUnit(result.worker?.distanceUnit);
      const beyond = result.worker
        ? Math.max(
            0,
            Math.ceil(milesIn(result.distanceMiles, unit) - result.worker.serviceRadiusMiles),
          )
        : null;
      const fee = formatMoney(result.travelFee ?? 0);
      return {
        label:
          beyond !== null
            ? `${beyond} ${distanceWord(unit, beyond !== 1)} outside the area · ${fee} travel fee`
            : `Outside the area · ${fee} travel fee`,
        tone: 'warning',
      };
    }
    default:
      return {
        label: result.notBookableReason ?? 'Does not serve this address',
        tone: 'neutral',
      };
  }
}

/** "92% on time"; nothing until the worker has 5 jobs (D17). */
export function onTimeLabel(rate: number | null | undefined): string | null {
  return rate === null || rate === undefined ? null : `${Math.round(rate * 100)}% on time`;
}

// ─── the hold (D12, D31) ────────────────────────────────────────────────────

/** Stripe holds last 7 days, so a card is held at most 5 days before the job (D12). */
export const HOLD_DAYS_BEFORE = 5;

export function holdsNow(serviceDate: string, now: DateTime = DateTime.local()): boolean {
  const day = DateTime.fromISO(serviceDate);
  if (!day.isValid) return true;
  return day.startOf('day').diff(now.startOf('day'), 'days').days <= HOLD_DAYS_BEFORE;
}

export function holdTimingText(serviceDate: string, now?: DateTime): string {
  return holdsNow(serviceDate, now)
    ? 'The job is within 5 days, so we hold the amount on your card now.'
    : 'We save your card now and hold the amount 5 days before the job.';
}

/** D31: "Hold $X, of which up to $Y covers add-ons you approve on site." */
export function holdText(quote: { authorizedAmount: number; addOnAllowance: number }): string {
  const hold = formatMoney(quote.authorizedAmount);
  if (!(quote.addOnAllowance > 0)) {
    return `We hold ${hold} on your card. You are charged after the job is done.`;
  }
  return `We hold ${hold} on your card. Up to ${formatMoney(quote.addOnAllowance)} of it covers add-ons you approve on site; what is not used is released after the job.`;
}

export const UNINSURED_NOTE =
  'This Provider is not insured. If something is damaged, you can only claim from the Provider through the Resolution Center.';

// ─── D22: supplies neither side has (mirrors the backend `suppliesWarnings`) ──

export interface WorkerSupplies {
  cleaningProducts?: boolean;
  tools?: boolean;
  vacuum?: boolean;
  linens?: boolean;
  guestConsumables?: boolean;
}

export interface HomeSupplies {
  vacuum?: boolean;
  washer?: boolean;
  dryer?: boolean;
  linenSets?: number | null;
  linensLocation?: string | null;
  consumablesLocation?: string | null;
}

export function suppliesWarnings(
  worker: WorkerSupplies | null | undefined,
  home: HomeSupplies | null | undefined,
): string[] {
  const w = worker ?? {};
  const h = home ?? {};
  const warnings: string[] = [];
  if (!w.vacuum && !h.vacuum) {
    warnings.push(
      'Neither you nor the worker has a vacuum. Leave one out or pick a worker who brings one.',
    );
  }
  if (!w.linens && !(h.linenSets && h.linenSets > 0)) {
    warnings.push(
      'No fresh linens: the worker does not bring them and you have no spare sets listed.',
    );
  }
  if (!w.guestConsumables && !h.consumablesLocation) {
    warnings.push(
      'Guest supplies (toilet paper, soap) are not brought by the worker. Say where yours are kept.',
    );
  }
  if (!w.cleaningProducts) {
    warnings.push(
      'The worker does not bring cleaning products. Leave yours where they can find them.',
    );
  }
  return warnings;
}

// ─── items for a quote or a job ─────────────────────────────────────────────

export interface BookOffering {
  id: string;
  serviceType: ServiceType;
  isActive: boolean;
}

/** Which services fit a site: a home gets a turnover; a vehicle a cleaning and a handover. */
export function servicesForSite(kind: ServiceSiteKind): ServiceType[] {
  return kind === ServiceSiteKind.Home
    ? [ServiceType.HomeTurnover]
    : [ServiceType.VehicleCleaning, ServiceType.VehicleHandover];
}

export interface ItemChoices {
  /** Services picked on the book page (a vehicle can have a cleaning and a handover, D27). */
  serviceTypes: ServiceType[];
  /** `sqft` is in `areaUnit` (a saved home's own; spec 028). */
  homeSize?: {
    bedrooms: number;
    bathrooms: number;
    sqft?: number | null;
    areaUnit?: string | null;
  } | null;
  sameDayTurnover?: boolean;
  vehicleClass?: VehicleClass | null;
  cleaningLevel: CleaningLevel;
  handoverHours: number;
}

/** The `JobItemInput`s of the picked services, one per offering. */
export function jobItems(
  offerings: readonly BookOffering[],
  choices: ItemChoices,
): IJobItemInput[] {
  const items: IJobItemInput[] = [];
  for (const type of choices.serviceTypes) {
    const offering = offerings.find(o => o.serviceType === type && o.isActive);
    if (!offering) continue;
    const item: IJobItemInput = { offeringId: offering.id };
    if (type === ServiceType.HomeTurnover && choices.homeSize) {
      item.homeSize = {
        bedrooms: choices.homeSize.bedrooms,
        bathrooms: choices.homeSize.bathrooms,
        // The API takes square feet here (D3).
        sqft: sqftForSearch(choices.homeSize.sqft, choices.homeSize.areaUnit),
        sameDayTurnover: Boolean(choices.sameDayTurnover),
      };
    }
    if (type === ServiceType.VehicleCleaning) {
      if (choices.vehicleClass) item.vehicleClass = choices.vehicleClass;
      item.cleaningLevel = choices.cleaningLevel;
    }
    if (type === ServiceType.VehicleHandover) item.maxBillableMinutes = choices.handoverHours * 60;
    items.push(item);
  }
  return items;
}

/** The site's stored vehicle class (`suv_truck`) as the API enum (`SUV_TRUCK`). */
export function vehicleClassOf(details: unknown): VehicleClass | null {
  const raw = (details as { vehicleClass?: string } | null)?.vehicleClass;
  const upper = typeof raw === 'string' ? raw.toUpperCase() : '';
  return (Object.values(VehicleClass) as string[]).includes(upper) ? (upper as VehicleClass) : null;
}

// ─── jobs ───────────────────────────────────────────────────────────────────

const CANCELLED = new Set<BookingStatus>([
  BookingStatus.CancelledByGuestMoreThan_24Hours,
  BookingStatus.CancelledByGuestLessThan_24HoursBeforeTripStart,
  BookingStatus.CancelledByGuestAfterTripStart,
  BookingStatus.CancelledByHost,
  BookingStatus.CancelledByAdmin,
  BookingStatus.DeclinedByHost,
  BookingStatus.RequestExpired,
  BookingStatus.RequestWithdrawn,
]);

const ON_SITE = new Set<JobStatus>([JobStatus.EnRoute, JobStatus.Arrived, JobStatus.InProgress]);
const DONE = new Set<JobStatus>([
  JobStatus.Completed,
  JobStatus.CustomerConfirmed,
  JobStatus.Disputed,
  JobStatus.Resolved,
]);

export type JobGroup = 'upcoming' | 'inProgress' | 'past' | 'cancelled';

export function jobGroup(
  job: Pick<ServiceJob, 'status' | 'serviceJob'>,
  now = Date.now(),
): JobGroup {
  if (CANCELLED.has(job.status)) return 'cancelled';
  const jobStatus = job.serviceJob?.jobStatus ?? null;
  if (jobStatus && ON_SITE.has(jobStatus)) return 'inProgress';
  if ((jobStatus && DONE.has(jobStatus)) || job.status === BookingStatus.Completed) return 'past';
  const end = job.serviceJob?.windowEndAt;
  if (end && end < now && job.status !== BookingStatus.Booked) return 'past';
  return 'upcoming';
}

export function jobStatusView(job: Pick<ServiceJob, 'status' | 'serviceJob'>): {
  label: string;
  tone: BadgeTone;
} {
  switch (job.status) {
    case BookingStatus.Pending:
      return { label: 'Waiting for payment', tone: 'warning' };
    case BookingStatus.CardDeclined:
      return { label: 'Card declined', tone: 'error' };
    case BookingStatus.Requested:
      return { label: 'Waiting for the worker', tone: 'warning' };
    case BookingStatus.DeclinedByHost:
      return { label: 'Declined', tone: 'neutral' };
    case BookingStatus.RequestExpired:
      return { label: 'Not answered in time', tone: 'neutral' };
    case BookingStatus.RequestWithdrawn:
      return { label: 'Withdrawn', tone: 'neutral' };
    case BookingStatus.CancelledByHost:
      return { label: 'Cancelled by the Provider', tone: 'error' };
    case BookingStatus.CancelledByAdmin:
      return { label: 'Cancelled', tone: 'error' };
    case BookingStatus.CancelledByGuestMoreThan_24Hours:
    case BookingStatus.CancelledByGuestLessThan_24HoursBeforeTripStart:
    case BookingStatus.CancelledByGuestAfterTripStart:
      return { label: 'Cancelled by you', tone: 'neutral' };
    default:
      break;
  }
  switch (job.serviceJob?.jobStatus) {
    case JobStatus.EnRoute:
      return { label: 'On the way', tone: 'info' };
    case JobStatus.Arrived:
      return { label: 'Arrived', tone: 'info' };
    case JobStatus.InProgress:
      return { label: 'In progress', tone: 'info' };
    case JobStatus.Completed:
      return { label: 'Done: please confirm', tone: 'warning' };
    case JobStatus.CustomerConfirmed:
      return { label: 'Confirmed', tone: 'success' };
    case JobStatus.Disputed:
      return { label: 'Problem reported', tone: 'error' };
    case JobStatus.Resolved:
      return { label: 'Resolved', tone: 'neutral' };
    default:
      return { label: 'Booked', tone: 'success' };
  }
}

/** A request or a booked job the customer may still cancel or withdraw. */
export const canWithdraw = (job: Pick<ServiceJob, 'status'>) =>
  job.status === BookingStatus.Requested;

export function canCancel(job: Pick<ServiceJob, 'status' | 'serviceJob'>): boolean {
  if (job.status !== BookingStatus.Booked) return false;
  const jobStatus = job.serviceJob?.jobStatus ?? null;
  return !jobStatus || !DONE.has(jobStatus);
}

/** A request that ended without a worker: offer to search again (W16). */
export const canFindAnother = (job: Pick<ServiceJob, 'status'>) =>
  job.status === BookingStatus.RequestExpired || job.status === BookingStatus.DeclinedByHost;

/** "Tue, Oct 20 · 10:00 AM to 2:00 PM MST", in the site's zone. */
export function windowText(startAt: number, endAt: number, zone: string): string {
  const start = DateTime.fromMillis(startAt, { zone });
  const end = DateTime.fromMillis(endAt, { zone });
  return `${start.toFormat('ccc, LLL d')} · ${start.toFormat('h:mm a')} to ${end.toFormat('h:mm a ZZZZ')}`;
}

/** "in 3 h 20 min", "in 2 days", "now". */
export function countdownText(targetMs: number, nowMs = Date.now()): string {
  const minutes = Math.round((targetMs - nowMs) / 60000);
  if (minutes <= 0) return 'now';
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `in ${hours} h${minutes % 60 ? ` ${minutes % 60} min` : ''}`;
  return `in ${Math.floor(hours / 24)} days`;
}

/** Labels of the services on a job, from the locked items. */
export function jobItemLabels(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  return items.map((item: any) => String(item?.label ?? item?.serviceType ?? 'Service'));
}

const SERVICE_FROM_KEY: Record<string, ServiceType> = {
  home_turnover: ServiceType.HomeTurnover,
  vehicle_cleaning: ServiceType.VehicleCleaning,
  vehicle_handover: ServiceType.VehicleHandover,
};

/**
 * The search to run again with a request's own conditions (W16: "Find another
 * worker"), searched by the site's ZIP code.
 */
export function rebookSearch(job: ServiceJob): ServiceSearchState {
  const base = defaultServiceSearch();
  const info = job.serviceJob;
  if (!info) return base;
  const items = Array.isArray(info.items) ? (info.items as any[]) : [];
  const first = items[0] ?? {};
  const serviceType =
    SERVICE_FROM_KEY[String(first.serviceType ?? '').toLowerCase()] ?? base.serviceType;
  const zone = info.timezone;
  const state: ServiceSearchState = {
    ...base,
    serviceType,
    where: info.site?.zip ?? '',
    zip: info.site?.zip ?? undefined,
    date: info.serviceDate || base.date,
    windowStart: DateTime.fromMillis(info.windowStartAt, { zone }).toFormat('HH:mm'),
    windowEnd: DateTime.fromMillis(info.windowEndAt, { zone }).toFormat('HH:mm'),
  };
  if (first.homeSize) {
    state.bedrooms = Number(first.homeSize.bedrooms ?? base.bedrooms);
    state.bathrooms = Number(first.homeSize.bathrooms ?? base.bathrooms);
  }
  const vehicleClass = vehicleClassOf(first);
  if (vehicleClass) state.vehicleClass = vehicleClass;
  const level = String(first.cleaningLevel ?? '').toUpperCase();
  if ((Object.values(CleaningLevel) as string[]).includes(level)) {
    state.cleaningLevel = level as CleaningLevel;
  }
  if (info.maxBillableMinutes) state.handoverHours = Math.round(info.maxBillableMinutes / 60);
  return state;
}

export const JOBS_PATH = '/services/jobs';
export const jobPath = (bookingId: string) => `${JOBS_PATH}/${bookingId}`;
export const SITES_PATH = '/services/sites';

/** D8, W25: how a handover is billed. */
export function handoverCapText(hours: number): string {
  return `Billed by actual time, at most ${hours} ${hours === 1 ? 'hour' : 'hours'}. At least 1 hour.`;
}

/**
 * W26: the handover clock on a job: billable minutes so far against the cap,
 * and a reminder near or at the cap (D30).
 */
export function handoverClock(
  minutesSoFar: number | null | undefined,
  maxBillableMinutes: number | null | undefined,
): { text: string; tone: 'info' | 'warning' | 'error'; percent: number } | null {
  if (minutesSoFar == null || !maxBillableMinutes) return null;
  const used = Math.max(0, Math.round(minutesSoFar));
  const percent = Math.min(100, Math.round((used / maxBillableMinutes) * 100));
  const label = (m: number) => {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return [h ? `${h} h` : '', r || !h ? `${r} min` : ''].filter(Boolean).join(' ');
  };
  const base = `${label(used)} of ${label(maxBillableMinutes)}`;
  if (used >= maxBillableMinutes) {
    return {
      text: `${base}. The limit is reached: the worker stops billing. Extend it to keep them longer.`,
      tone: 'error',
      percent,
    };
  }
  if (maxBillableMinutes - used <= 30) {
    return {
      text: `${base}. ${label(maxBillableMinutes - used)} left before the limit.`,
      tone: 'warning',
      percent,
    };
  }
  return { text: base, tone: 'info', percent };
}
